import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Typography } from "@mui/material";
import moment from "moment";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import {
  deleteDiagnosticsByIdWoLoader,
  getPetsCompliantSummaryWoLoader,
} from "../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../util/AppColors";
import { doneAtList, modalities, sampleTypes } from "../../../../util/dropList";
import { diagStatusEnum, genderEnum } from "../../../../util/enum";
import { getPetAgeDisplay } from "../../../../util/getAge";
import { canWriteModule } from "../../../../util/roleModuleAccess";
import DiagnosticAddUpdate from "../../../CommonScreens/Diagnostic/DiagnosticAddUpdate";
import DiagnosticReport from "../../../CommonScreens/Diagnostic/DiagnosticReport";

const addIcon = { color: AppColors.appPrimary, width: 15, height: 15 };
const plusBtn = { color: AppColors.appPrimary, width: 20, height: 20 };
const delBtn = { color: AppColors.red, width: 20, height: 20 };

const nameExpan = {
  type: "Select Type",
  testName: "Test Name",
  variant: "Variant",
  dueDate: "Due Date",
  remarks: "Remarks",
  doneAt: "Done At",
  sampleType: "Sample Type",
  processingTime: "Processing Time",
  modality: "Modality",
  device: "Device",
  priority: "Priority",
  status: "Status",
  collectionTime: "Collection Time *",
  collectedBy: "Performed By *",
};

const initialValues = {
  type: "Lab",
  testName: null,
  dueDate: new Date(),
  remarks: "",
  files: [],
  price: 0,
  tax: 0,
  sampleCollected: false,
  doneAt: doneAtList?.[0]?.value,
  collectionTime: new Date(),
  priority: "Routine",
  sampleCollected: false,
  collectedBy: null,
  quantity: 1,
  // status: "In Progress",
};

const initialErrors = {
  type: false,
  dueDate: false,
  remarks: false,
  collectedBy: false,
};
const initialHelpers = { type: "", dueDate: "", remarks: "", collectedBy: "" };
const defaultFile = {
  question: "File Upload",
  questionType: "fileupload",
  required: false,
  answer: ["file"],
  vitalGroupId: null,
  vitalId: null,
  delete: false,
  questionId: null,
};
const ckEditorBuild =
  typeof ClassicEditor?.create === "function"
    ? ClassicEditor
    : ClassicEditor?.default;

const PetDiagnostics = () => {
  const dispatch = useDispatch();
  const canManageDashboard = canWriteModule("dashboard");
  const location = useLocation();
  const { appointment } = location.state || {};
  const [modVisible, setModVisible] = useState(false);
  const [isEditModal, setEditModal] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [diagnosticsId, setDiagnosticsId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tempSelectedItem, setTempSelectedItem] = useState(null);
  const [diagValues, setDiagValues] = useState(initialValues);
  const [delModVsble, setDelModVsble] = useState(false);
  const [reportDownVsble, setReportDownVsble] = useState(false);
  const [diagnosticsHelpers, setDiagnosticsHelpers] = useState(initialHelpers);
  const [diagnosticsErrors, setDiagnosticsErrors] = useState(initialErrors);
  const [tempModVsble, setTempModVsble] = useState(false);
  const [templatevalues, setTemplateValues] = useState({});
  const {
    diagTemplateById,
    vitalsByIds,
    details: clinicDetails,
  } = useSelector((state) => state?.clinic);
  // const vets = useSelector((state) => state?.vet?.vets);
  const apntDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const {
    services: clinicServiceList,
    vets: vetList,
    clinic: clinicDataDet,
  } = useMemo(() => {
    if (!mandateDataRaw) {
      return { services: [], vets: [], clinic: {} };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        services: Array.isArray(reqData?.services) ? reqData?.services : [],
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
        clinic: reqData?.clinic ?? {},
      };
    } catch {
      return { services: [], vets: [], clinic: {} };
    }
  }, [mandateDataRaw]);
  const services = clinicServiceList ?? [];
  const vets = vetList ?? [];
  const clinicDet = clinicDataDet?.id ? clinicDataDet : clinicDetails;

  const callApi = () => {
    dispatch(
      getPetsCompliantSummaryWoLoader(appointment?.appoinment?.appointmentId),
    );
  };

  const getTableRecords = () => {
    return apntDet?.diagnostics
      ?.filter(
        (li) =>
          li?.appoinment?.appointmentId === apntDet?.appointment?.appointmentId,
      )
      ?.sort((a, b) => {
        const aid = Number(a?.diagonosticId);
        const bid = Number(b?.diagonosticId);
        return (
          (Number.isFinite(aid) ? aid : 0) - (Number.isFinite(bid) ? bid : 0)
        );
      });
  };

  const diagnosticReportHeaderRows = useMemo(() => {
    const pet = apntDet?.pet;
    const appt = apntDet?.appointment;
    const referredRaw = appt?.doctorName;
    const referredBy = Array.isArray(referredRaw)
      ? referredRaw?.[0]
      : referredRaw;
    let loggedInName = "-";
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const p = JSON.parse(raw);
        loggedInName =
          [p?.salutation, p?.name].filter(Boolean).join(" ").trim() ||
          p?.userName ||
          p?.email ||
          "-";
      }
    } catch {
      loggedInName = "-";
    }
    const agePart = getPetAgeDisplay(pet?.dob);
    const sexPart = genderEnum?.[pet?.gender ?? ""];
    const ageSex =
      agePart && sexPart
        ? `${agePart} / ${sexPart}`
        : agePart || sexPart || "-";

    return [
      {
        leftLabel: "Lab Order ID:",
        leftValue:
          tempSelectedItem?.labOrderId ?? tempSelectedItem?.labOrderNo ?? "-",
        rightLabel: "Date:",
        rightValue: tempSelectedItem?.dueDate
          ? moment(tempSelectedItem.dueDate).format("DD-MM-YYYY")
          : "-",
      },
      {
        leftLabel: "Patient Reg. No:",
        leftValue: pet?.registrationNo ?? appt?.registrationNo ?? "-",
        rightLabel: "Vet Name:",
        rightValue: referredBy ?? "-",
      },
      {
        leftLabel: "Parent Name:",
        leftValue: pet?.userName ?? "-",
        rightLabel: "Species:",
        rightValue: pet?.petType ?? "-",
      },
      {
        leftLabel: "Pet Name:",
        leftValue: pet?.petName ?? "-",
        rightLabel: "Breed:",
        rightValue: pet?.breed ?? "-",
      },
      {
        leftLabel: "Lab Technician / Pathologist:",
        leftValue: loggedInName,
        rightLabel: "Age / Sex:",
        rightValue: ageSex,
      },
    ];
  }, [apntDet, tempSelectedItem]);

  const handleModClose = (typ = null) => {
    if (typ === "apiSuccess") callApi();
    setModVisible(false);
    setEditModal(false);
    setSelectedItem(null);
    setDiagnosticsId(null);
  };

  const getServiceDetById = (servId, vrntId = null) => {
    if (!servId) return null;
    const reqServ = services?.find((ser) => ser?.serviceId === servId);
    if (vrntId) {
      return (
        reqServ?.serviceVariants?.find((vnt) => vnt?.variantId === vrntId) ?? {}
      );
    }
    return reqServ;
  };

  const getVariantsByServiceId = (servId = null) => {
    if (!servId) return [];
    const servDet = getServiceDetById(servId);
    if (servDet) {
      const clinicVariants = servDet?.serviceVariants?.filter(
        (vl) => vl?.createdBy === clinicDataDet?.id && vl?.approved,
      );
      return clinicVariants?.map((vnt) => ({
        ...vnt,
        name: vnt?.variantName,
        value: vnt?.variantId,
      }));
    }
    return [];
  };

  const handleDelete = (diag) => {
    setDiagnosticsId(diag?.diagonosticId);
    setDelModVsble(true);
  };

  const handleEditModal = (diagnostics) => {
    setDiagnosticsId(diagnostics?.diagonosticId);
    setSelectedItem(diagnostics);
    const reqDia = getServiceDetById(diagnostics?.serviceId);
    const [hour, min] =
      diagnostics?.collectionTime?.length > 0
        ? diagnostics?.collectionTime?.split(":")
        : ["00:00"];

    const reqObj = {
      type: diagnostics?.type,
      testName: diagnostics?.testName,
      remarks: diagnostics?.remark,
      dueDate: diagnostics?.dueDate,
      price: reqDia?.servicePrice ?? reqDia?.sellPrice ?? 0,
      tax: reqDia?.tax ?? 0,
      serviceId: reqDia?.serviceId,
      files: diagnostics?.file?.map((fle) => ({ previewUrl: fle })),
      doneAt: diagnostics?.doneAt,
      sampleCollected: diagnostics?.sampleCollected === "Y",
      priority: diagnostics?.priority,
      status: diagnostics?.status,
      collectionTime: new Date(
        new Date().setHours(hour ? hour : 0, min ? min : 0),
      ),
      hsnSacCode: reqDia?.hsnSacCode ?? null,
      collectedBy: null,
      quantity: diagnostics?.quantity ?? 1,
    };
    if (diagnostics?.variantId) {
      reqObj.variantId = diagnostics?.variantId;
      const vnts = getVariantsByServiceId(
        reqDia?.serviceId ?? diagnostics?.serviceId,
      );
      if (vnts?.length > 0) {
        const reqVar = vnts?.find(
          (vt) => vt?.variantId === diagnostics?.variantId,
        );
        if (reqVar) {
          reqObj.variantId = reqVar?.variantId;
          reqObj.variantName = reqVar?.variantName;
          reqObj.variant = reqVar?.variantId;
          reqObj.price = reqVar?.servicePrice;
          reqObj.tax = reqVar?.tax ?? 0;
        }
      }
    }
    if (diagnostics?.collectedById) {
      reqObj.collectedBy = {
        name: diagnostics?.collectedByName,
        value: diagnostics?.collectedById,
      };
    }
    setDiagValues(reqObj);
    setModVisible(true);
    setEditModal(true);
  };

  const onCloseDelMod = () => {
    setDelModVsble(false);
    setDiagnosticsId(null);
  };

  const onCofirmDelete = () => {
    if (!diagnosticsId) return;
    dispatch(deleteDiagnosticsByIdWoLoader(diagnosticsId)).then((res) => {
      if (res?.payload) {
        onCloseDelMod();
        callApi();
      }
    });
  };

  const getReqSampleValue = (itm) => {
    return sampleTypes?.find((st) => st?.value === itm)?.name;
  };

  const getReqModalValue = (itm) => {
    return modalities?.find((st) => st?.value === itm)?.name;
  };

  const handleEnterResult = (itm) => {
    setSelectedItem(itm);
    setModVisible(true);
  };

  const onCloseTempMod = () => {
    setTempModVsble(false);
    setTemplateValues({});
    setTempSelectedItem(null);
  };

  const handleChange = (name, value) => {
    setTemplateValues({ ...templatevalues, [name]: value });
  };

  const handleTempImageRemove = (name, ind) => {
    const reqFiles = templatevalues?.[name]?.filter((fl, i) => i !== ind);
    setTemplateValues({ ...templatevalues, [name]: reqFiles });
  };

  const onDownloadReport = (itm) => {
    setTempSelectedItem(itm);
    setReportDownVsble(true);
  };

  const onCloseDownMod = () => {
    setReportDownVsble(false);
    setTempSelectedItem(null);
  };

  const renderTableRecord = () => {
    const tdLabel = {
      width: "22%",
      backgroundColor: "#f8f9fb",
      fontWeight: 600,
      color: "#1a1a1a",
      padding: "10px 12px",
      border: "1px solid #dadce0",
      fontSize: 14,
      verticalAlign: "middle",
      boxSizing: "border-box",
    };
    const tdValue = {
      width: "28%",
      backgroundColor: "#fff",
      fontWeight: 400,
      color: "#1a1a1a",
      padding: "10px 12px",
      border: "1px solid #dadce0",
      fontSize: 14,
      verticalAlign: "middle",
      boxSizing: "border-box",
    };
    return (
      <table
        className="diagnostic-report-native-table"
        style={{
          borderCollapse: "collapse",
          width: "100%",
          fontSize: 14,
        }}
      >
        <tbody>
          {diagnosticReportHeaderRows?.map((row, idx) => (
            <tr key={idx}>
              <td style={tdLabel}>{row?.leftLabel}</td>
              <td style={tdValue}>{row?.leftValue || "-"}</td>
              <td style={tdLabel}>{row?.rightLabel}</td>
              <td style={tdValue}>{row?.rightValue || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderCbcResultsTableUi = () => (
    <div className="diagnostic-cbc-results-wrap">
      <table className="diagnostic-cbc-results-table">
        <thead>
          <tr>
            <th>Test Description</th>
            {tempSelectedItem?.data?.some(
              (li) => li?.inputType === "Numeric",
            ) ? (
              <th>Units</th>
            ) : null}
            <th>Result</th>
            {tempSelectedItem?.data?.some(
              (li) => li?.inputType === "Numeric",
            ) ? (
              <th>Reference Range</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {tempSelectedItem?.data
            ?.filter((li) => li?.questionType !== "fileupload" && li?.vitalId)
            ?.map((row, i) => {
              const lower = row?.lowerRange ?? "";
              const higher = row?.upperRange ?? "";
              let refRange = "";
              if (lower?.length > 0 || higher?.length > 0) {
                refRange = `${lower} - ${higher}`;
              }
              return (
                <tr key={`${Date.now()}_${i}`}>
                  <td>{row?.question}</td>
                  {tempSelectedItem?.data?.some(
                    (li) => li?.inputType === "Numeric",
                  ) ? (
                    <td>{row?.unit?.length ? row?.unit : "\u00A0"}</td>
                  ) : null}
                  <td>
                    {row?.answer?.length
                      ? Array.isArray(row?.answer)
                        ? row?.answer?.join(", ")
                        : (row?.answer ?? "")
                      : "\u00A0"}
                  </td>
                  {tempSelectedItem?.data?.some(
                    (li) => li?.inputType === "Numeric",
                  ) ? (
                    <td>{refRange?.length ? refRange : "\u00A0"}</td>
                  ) : null}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="ph20 mt10">
        <div className="flex-row-ali-cen ph20" style={{ flexWrap: "nowrap" }}>
          <div className="w25Per">
            <Typography className="font-bold blue-color fs12">Name</Typography>
          </div>
          <div className="w10Per">
            <Typography className="font-bold blue-color fs12">Type</Typography>
          </div>
          <div className="w12Per">
            <Typography className="font-bold blue-color fs12">
              Billable Qty
            </Typography>
          </div>
          <div className="w15Per">
            <Typography className="font-bold blue-color fs12">
              Sample/Modality
            </Typography>
          </div>
          <div className="w18Per" style={{ flexShrink: 0 }}>
            <Typography className="font-bold blue-color fs12">
              Status
            </Typography>
          </div>
          <div className="w20Per" style={{ flexShrink: 0 }} />
        </div>
        <div className="blue-hor-split mt5" />
        {getTableRecords()?.length > 0
          ? getTableRecords()?.map((diag) => {
              return (
                <div
                  className="flex-row-ali-cen ph20 mt10"
                  key={diag?.diagonosticId}
                  style={{ flexWrap: "nowrap" }}
                >
                  <div className="w25Per">
                    <Typography className="txt-semi-bold blue-color fs12">
                      {diag?.testName}
                    </Typography>
                  </div>
                  <div className="w10Per">
                    <Typography className="font-medium gray34 fs12">
                      {diag?.type}
                    </Typography>
                  </div>
                  <div className="w12Per">
                    <Typography className="font-medium gray34 fs12">
                      {diag?.quantity}
                    </Typography>
                  </div>
                  <div className="w15Per">
                    <Typography className="font-medium gray34 fs12">
                      {diag?.sampleCollected
                        ? diag?.type === "Lab"
                          ? getReqSampleValue(
                              getServiceDetById(diag?.serviceId)?.sampleType,
                            )
                          : (getReqModalValue(
                              getServiceDetById(diag?.serviceId)?.modality,
                            ) ?? "")
                        : "Not Yet"}
                    </Typography>
                  </div>
                  <div className="w18Per" style={{ flexShrink: 0 }}>
                    <div
                      className={`fs12 con-fit
                  ${
                    (diag?.status ?? "")?.toLowerCase() === "completed"
                      ? "completed2"
                      : (diag?.status ?? "")?.toLowerCase() === "pending"
                        ? "pending2"
                        : "overdue2"
                  }`}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {diagStatusEnum?.[diag?.status]}
                    </div>
                  </div>
                  <div
                    className="w20Per flex-end"
                    style={{ alignItems: "center", flexShrink: 0 }}
                  >
                    {/* {apntDet?.appointment?.appointmentStatus?.toLowerCase() !==
                      "completed" ||
                    (apntDet?.appointment?.appointmentStatus?.toLowerCase() ===
                      "completed" &&
                      !apntDet?.appointment?.lock) ? ( */}
                    <div className="flex-row-ali-cen">
                      {canManageDashboard &&
                      diag?.sampleCollected === "Y" &&
                      [
                        "pending review",
                        "in progress",
                        "patient review",
                      ]?.includes((diag?.status ?? "")?.toLowerCase()) ? (
                        <div
                          className="cursor"
                          onClick={() => handleEnterResult(diag)}
                        >
                          <Typography className="blue-color fs12 txt-semi-bold blu-back white-color ph10 pv5 br5">
                            {diag?.data?.length > 0 ? "Modify" : "Enter"} Result
                          </Typography>
                        </div>
                      ) : null}
                      {canManageDashboard &&
                      (diag?.status ?? "")?.toLowerCase() === "scheduled" ? (
                        <div
                          className="cursor"
                          onClick={() => handleEditModal(diag)}
                        >
                          <Typography className="blue-color fs12 txt-semi-bold blu-back white-color ph10 pv5 br5">
                            Study Performed
                            {/* {diag?.type === "Lab"
                                ? "Collect Sample"
                                : "Take Test"} */}
                          </Typography>
                        </div>
                      ) : null}
                    </div>
                    {/* ) : null} */}
                    {(diag?.status ?? "")?.toLowerCase() === "completed" ? (
                      <div
                        className="cursor"
                        onClick={() => onDownloadReport(diag)}
                      >
                        <Typography className="blue-color fs12 txt-semi-bold blu-back white-color ph10 pv5 br5">
                          Print Report {/* Download */}
                        </Typography>
                      </div>
                    ) : null}
                    {canManageDashboard ? (
                      <div
                        className="cursor ml10"
                        onClick={() => handleDelete(diag)}
                      >
                        <div className="flex-center">
                          <DeleteOutlineIcon sx={delBtn} />
                        </div>
                        <Typography className="red3 fs8 txt-semi-bold text-align-center">
                          Delete
                        </Typography>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          : null}
        {/* {apntDet?.appointment?.appointmentStatus?.toLowerCase() !==
          "completed" ||
        (apntDet?.appointment?.appointmentStatus?.toLowerCase() ===
          "completed" &&
          !apntDet?.appointment?.lock) ? ( */}
        {canManageDashboard ? (
          <div
            className="flex-row-ali-cen mt20 cursor"
            onClick={() => setModVisible(true)}
          >
            <div className="flex-center plus-icon">
              <AddIcon sx={addIcon} />
            </div>
            <Typography className="blue-color fs16 ml5 font-bold">
              Add
            </Typography>
          </div>
        ) : null}
        {/* ) : null} */}
      </div>
      <DiagnosticReport
        reportDownVsble={reportDownVsble}
        onCloseDownMod={onCloseDownMod}
        selectedItem={tempSelectedItem}
      />

      <DiagnosticAddUpdate
        modVisible={modVisible}
        handleModClose={handleModClose}
        selectedItem={selectedItem}
      />

      <CustomModal
        open={delModVsble}
        onClose={onCloseDelMod}
        header="Delete"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete the diagnostics?
        </Typography>

        <div className="clinic-mod-btn-pos mb10 mt15">
          <div className="mr10">
            <CustomButton text="Yes" redBtn onClick={onCofirmDelete} smallBtn />
          </div>
          <div className="ml10">
            <CustomButton text="No" onClick={onCloseDelMod} smallBtn />
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default PetDiagnostics;
